import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { useConsumer } from '@/hooks/useConsumer';

const orNull = (v: string) => {
  const t = v.trim();
  return t === '' ? null : t;
};

/**
 * Dados pessoais do cliente — espelha o modelo do prestador (DadosPessoaisCard):
 * Email só-leitura (vem do Auth); Nome, Telefone e Endereço (morada/código
 * postal/cidade/país) editáveis. Grava em `consumers` via useConsumer.
 */
export function ConsumerDadosPessoaisCard() {
  const { t } = useTranslation();
  const { consumer, updateConsumer, isSaving } = useConsumer();

  const [telefone, setTelefone] = useState('');
  const [morada, setMorada] = useState('');
  const [codigoPostal, setCodigoPostal] = useState('');
  const [cidade, setCidade] = useState('');
  const [pais, setPais] = useState('');

  useEffect(() => {
    if (consumer) {
      setTelefone(consumer.telefone ?? '');
      setMorada(consumer.morada ?? '');
      setCodigoPostal(consumer.codigo_postal ?? '');
      setCidade(consumer.cidade ?? '');
      setPais(consumer.pais ?? '');
    }
  }, [consumer]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateConsumer({
      telefone: orNull(telefone),
      morada: orNull(morada),
      codigo_postal: orNull(codigoPostal),
      cidade: orNull(cidade),
      pais: orNull(pais),
    });
  };

  return (
    <form className="ow-card max-w-md mx-auto w-full" onSubmit={onSubmit}>
      <div className="px-6 pt-5 pb-3">
        <h3 className="text-base font-bold tracking-tight">{t('personalDataForm.title')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{t('personalDataForm.description')}</p>
      </div>
      <div className="px-6 pb-5 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t('common.name')}</Label>
          <Input value={consumer?.nome ?? ''} disabled readOnly />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{t('common.email')}</Label>
          <Input value={consumer?.email ?? ''} disabled readOnly />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cdp-telefone" className="text-xs text-muted-foreground">{t('common.phone')}</Label>
          <PhoneInput id="cdp-telefone" value={telefone} onChange={setTelefone} defaultCountry="PT" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cdp-morada" className="text-xs text-muted-foreground">{t('personalDataForm.address')}</Label>
          <Input
            id="cdp-morada"
            value={morada}
            onChange={(e) => setMorada(e.target.value)}
            placeholder={t('personalDataForm.addressPlaceholder')}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cdp-codigo-postal" className="text-xs text-muted-foreground">{t('personalDataForm.postalCode')}</Label>
          <Input id="cdp-codigo-postal" value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cdp-cidade" className="text-xs text-muted-foreground">{t('personalDataForm.city')}</Label>
          <Input id="cdp-cidade" value={cidade} onChange={(e) => setCidade(e.target.value)} autoComplete="address-level2" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cdp-pais" className="text-xs text-muted-foreground">{t('personalDataForm.country')}</Label>
          <Input id="cdp-pais" value={pais} onChange={(e) => setPais(e.target.value)} autoComplete="country-name" />
        </div>
      </div>
      <div className="px-6 pb-5">
        <Button type="submit" disabled={isSaving}>
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
